import type {
  CompareTo,
  ExtendWithOption,
  FunnelStageKey,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export type MonitoringPromptScope =
  | 'totalFunnel'
  | 'criticalElement'
  | 'topOfFunnel'
  | 'criticalElements';

export type MonitoringPromptView = 'volume' | 'rate' | 'any';

export type MonitoringPromptCard = FunnelStageKey | ExtendWithOption | null;

export interface MonitoringPromptCatalogEntry {
  id: string;
  scope: MonitoringPromptScope;
  compareTo: CompareTo;
  view: MonitoringPromptView;
  card: MonitoringPromptCard;
  text: string;
}

export const MONITORING_PROMPT_CATALOG: readonly MonitoringPromptCatalogEntry[] =
  [
    {
      id: 'monitoring-prompt-1',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources are driving the year-over-year decline in my inquiry pool?',
    },
    {
      id: 'monitoring-prompt-2',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'inquiry',
      text: 'What does my inquiry pool look like by consumer income band compared to last year?',
    },
    {
      id: 'monitoring-prompt-3',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources contributed the most new inquiries in the past week?',
    },
    {
      id: 'monitoring-prompt-4',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'inquiry',
      text: 'Is my year-over-year inquiry gap widening or narrowing compared to where it was 30 days ago?',
    },
    {
      id: 'monitoring-prompt-5',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources have gained or lost the most ground over the past month?',
    },
    {
      id: 'monitoring-prompt-6',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources grew or shrank the most across the full prior year?',
    },
    {
      id: 'monitoring-prompt-7',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'application',
      text: 'Which inquiry sources have the highest and lowest application rates this cycle?',
    },
    {
      id: 'monitoring-prompt-8',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'application',
      text: 'What is my average SAT, ACT, and GPA for my applicant pool this year vs. last year?',
    },
    {
      id: 'monitoring-prompt-9',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'application',
      text: 'Which inquiry sources drove the most new applications this past week?',
    },
    {
      id: 'monitoring-prompt-10',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'application',
      text: 'Which inquiry sources have driven the most application growth or decline over the past month?',
    },
    {
      id: 'monitoring-prompt-11',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'application',
      text: 'What was my final application volume last cycle, and which inquiry sources produced the most applicants?',
    },
    {
      id: 'monitoring-prompt-12',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'application',
      text: 'What was the average SAT, ACT, and GPA of my full applicant pool last cycle compared to the cycle before?',
    },
    {
      id: 'monitoring-prompt-13',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'admit',
      text: 'Is my admit rate declining, or is the drop in admits purely a function of fewer applicants?',
    },
    {
      id: 'monitoring-prompt-14',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'admit',
      text: 'How do my admits break down by distance band and income category compared to last year?',
    },
    {
      id: 'monitoring-prompt-15',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'admit',
      text: 'Are there any segments where admit activity has notably accelerated or stalled in the past week?',
    },
    {
      id: 'monitoring-prompt-16',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'admit',
      text: 'Have any segments (income, distance, source) seen a meaningful shift in admit volume over the last 30 days?',
    },
    {
      id: 'monitoring-prompt-17',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'admit',
      text: 'What was the academic profile (SAT, ACT, GPA) of my full admitted class last cycle vs. the cycle before?',
    },
    {
      id: 'monitoring-prompt-18',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'netDeposit',
      text: 'What is my point-in-time net deposit distribution by consumer income band compared to my cohort?',
    },
    {
      id: 'monitoring-prompt-19',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'netDeposit',
      text: 'Which student segments are depositing this week — are they different from the deposits I had 7 days ago?',
    },
    {
      id: 'monitoring-prompt-20',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'netDeposit',
      text: 'Has the composition of my deposited class (income, distance, academic profile) shifted over the last 30 days?',
    },
    {
      id: 'monitoring-prompt-21',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'netDeposit',
      text: 'How did the composition of my enrolled class (in-state vs. out-of-state, international, income) shift from the prior cycle?',
    },
    {
      id: 'monitoring-prompt-22',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'prospect',
      text: 'Which prospect sources are driving the year-over-year decline in my prospect pool?',
    },
    {
      id: 'monitoring-prompt-23',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'prospect',
      text: 'How does my prospect pool break down by distance band and consumer income compared to last year?',
    },
    {
      id: 'monitoring-prompt-24',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'prospect',
      text: 'Which prospect sources contributed the most new names this past week?',
    },
    {
      id: 'monitoring-prompt-25',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'prospect',
      text: 'How many prospects have I added in the last 30 days, and is that pace faster or slower than the same window last year?',
    },
    {
      id: 'monitoring-prompt-26',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'prospect',
      text: 'Which prospect sources grew or shrank the most across the full prior year?',
    },
    {
      id: 'monitoring-prompt-27',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'financial-aid',
      text: 'How does my FAFSA filer volume compare to the same point last year, and which segments show the largest decline?',
    },
    {
      id: 'monitoring-prompt-28',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'financial-aid',
      text: 'How does my filer volume break down by income band and distance compared to last year?',
    },
    {
      id: 'monitoring-prompt-29',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'How many new FAFSA filers appeared in the last 7 days, and how does that compare to the same week last year?',
    },
    {
      id: 'monitoring-prompt-30',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'Is my weekly filer pace picking up or still lagging behind last year?',
    },
    {
      id: 'monitoring-prompt-31',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'Is my year-over-year filer gap closing or widening compared to where it was 30 days ago?',
    },
    {
      id: 'monitoring-prompt-32',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'How many new filers have I gained in the last 30 days compared to the same window last year?',
    },
    {
      id: 'monitoring-prompt-33',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'financial-aid',
      text: 'How did my full-year filer volume break down by income band and distance for the completed cycle?',
    },
    {
      id: 'monitoring-prompt-34',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'retention',
      text: 'What is my current retention count, and how does my historical persistence rate compare to the prior cycle?',
    },
    {
      id: 'monitoring-prompt-35',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'retention',
      text: 'Which student segments (income, distance, academic profile) have the lowest retention rates?',
    },
    {
      id: 'monitoring-prompt-36',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'retention',
      text: 'Has my retention count changed in the last 7 days — have any students dropped or been added?',
    },
    {
      id: 'monitoring-prompt-37',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'retention',
      text: 'Are there specific segments where retention losses have accelerated this past week?',
    },
    {
      id: 'monitoring-prompt-38',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'retention',
      text: 'How has my retention count trended over the last 30 days — is attrition slowing or continuing?',
    },
    {
      id: 'monitoring-prompt-39',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'retention',
      text: 'Which student segments have seen the most retention movement over the past month?',
    },
    {
      id: 'monitoring-prompt-40',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'retention',
      text: 'Which student segments had the highest and lowest retention rates in the completed cycle?',
    },
    {
      id: 'monitoring-prompt-41',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'application',
      text: 'Which inquiry sources have the highest and lowest app rates, and how have those rates changed year over year?',
    },
    {
      id: 'monitoring-prompt-42',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'application',
      text: 'How does my app rate vary by consumer income band compared to last year?',
    },
    {
      id: 'monitoring-prompt-43',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'application',
      text: 'Has my app rate moved in the last 7 days, and which segments are driving the change?',
    },
    {
      id: 'monitoring-prompt-44',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'application',
      text: 'Are any inquiry sources showing a notable app rate spike or drop this past week?',
    },
    {
      id: 'monitoring-prompt-45',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'application',
      text: 'How has my app rate trended over the last 30 days — is it improving, flat, or declining?',
    },
    {
      id: 'monitoring-prompt-46',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'application',
      text: 'Which segments have seen the biggest app rate improvement or decline over the past month?',
    },
    {
      id: 'monitoring-prompt-47',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'application',
      text: 'Which inquiry sources had the highest and lowest full-year app rates last cycle?',
    },
    {
      id: 'monitoring-prompt-48',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'admit',
      text: 'Has my admit rate changed year over year, or is the decline in admits driven entirely by fewer applicants?',
    },
    {
      id: 'monitoring-prompt-49',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'admit',
      text: 'What is my admit rate for international students compared to domestic?',
    },
    {
      id: 'monitoring-prompt-50',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'admit',
      text: 'Has my admit rate shifted in the last 7 days — am I becoming more or less selective week over week?',
    },
    {
      id: 'monitoring-prompt-51',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'admit',
      text: 'Are there segments where my admit rate has moved notably this past week?',
    },
    {
      id: 'monitoring-prompt-52',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'admit',
      text: 'How has my admit rate moved over the last 30 days — am I trending more or less selective?',
    },
    {
      id: 'monitoring-prompt-53',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'admit',
      text: 'Which segments have seen the biggest admit rate shift over the past month?',
    },
    {
      id: 'monitoring-prompt-54',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'admit',
      text: 'What was my final admit rate last cycle — did I become more or less selective than the cycle before?',
    },
    {
      id: 'monitoring-prompt-55',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'admit',
      text: 'How did my admit rate vary by income band and distance for the full prior year?',
    },
    {
      id: 'monitoring-prompt-56',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'netDeposit',
      text: 'Which inquiry sources have the highest and lowest yield rates so far this cycle?',
    },
    {
      id: 'monitoring-prompt-57',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'netDeposit',
      text: 'How does my yield rate vary by in-state vs. out-of-state compared to last year?',
    },
    {
      id: 'monitoring-prompt-58',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'How has my yield rate changed in the last 7 days, and which segments are moving it?',
    },
    {
      id: 'monitoring-prompt-59',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'Are any income or distance segments showing a notable yield rate shift this past week?',
    },
    {
      id: 'monitoring-prompt-60',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'Is my yield rate gap vs. last year getting better or worse over the past month?',
    },
    {
      id: 'monitoring-prompt-61',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'How has my yield rate changed over the last 30 days, and which segments are driving the movement?',
    },
    {
      id: 'monitoring-prompt-62',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'netDeposit',
      text: 'Which inquiry sources had the highest and lowest full-year yield rates last cycle?',
    },
    {
      id: 'monitoring-prompt-63',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'inquiry',
      text: 'What is my inquiry rate (prospect-to-inquiry conversion), and how does it compare to the same point last year?',
    },
    {
      id: 'monitoring-prompt-64',
      scope: 'totalFunnel',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'inquiry',
      text: 'Which prospect sources have the highest and lowest inquiry conversion rates this cycle?',
    },
    {
      id: 'monitoring-prompt-65',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'inquiry',
      text: 'Has my inquiry rate shifted in the last 7 days — are new prospects converting to inquiries faster or slower?',
    },
    {
      id: 'monitoring-prompt-66',
      scope: 'totalFunnel',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'inquiry',
      text: 'Are any prospect sources showing a notable inquiry rate change this past week?',
    },
    {
      id: 'monitoring-prompt-67',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'inquiry',
      text: 'How has my inquiry rate trended over the last 30 days — is prospect-to-inquiry conversion improving?',
    },
    {
      id: 'monitoring-prompt-68',
      scope: 'totalFunnel',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'inquiry',
      text: 'Which prospect sources have seen the biggest inquiry rate shift over the past month?',
    },
    {
      id: 'monitoring-prompt-69',
      scope: 'totalFunnel',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'inquiry',
      text: 'Which prospect sources had the highest and lowest full-year inquiry conversion rates?',
    },
    {
      id: 'monitoring-prompt-70',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources are driving the year-over-year decline in my inquiry pool?',
    },
    {
      id: 'monitoring-prompt-71',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'inquiry',
      text: 'What does my inquiry pool look like by consumer income band compared to last year?',
    },
    {
      id: 'monitoring-prompt-72',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources contributed the most new inquiries in the past week?',
    },
    {
      id: 'monitoring-prompt-73',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'inquiry',
      text: 'Is my year-over-year inquiry gap widening or narrowing compared to where it was 30 days ago?',
    },
    {
      id: 'monitoring-prompt-74',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources have gained or lost the most ground over the past month?',
    },
    {
      id: 'monitoring-prompt-75',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'inquiry',
      text: 'Which inquiry sources grew or shrank the most across the full prior year?',
    },
    {
      id: 'monitoring-prompt-76',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'application',
      text: 'Which inquiry sources have the highest and lowest application rates this cycle?',
    },
    {
      id: 'monitoring-prompt-77',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'application',
      text: 'What is my average SAT, ACT, and GPA for my applicant pool this year vs. last year?',
    },
    {
      id: 'monitoring-prompt-78',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'application',
      text: 'Which inquiry sources drove the most new applications this past week?',
    },
    {
      id: 'monitoring-prompt-79',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'application',
      text: 'Which inquiry sources have driven the most application growth or decline over the past month?',
    },
    {
      id: 'monitoring-prompt-80',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'application',
      text: 'What was my final application volume last cycle, and which inquiry sources produced the most applicants?',
    },
    {
      id: 'monitoring-prompt-81',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'application',
      text: 'What was the average SAT, ACT, and GPA of my full applicant pool last cycle compared to the cycle before?',
    },
    {
      id: 'monitoring-prompt-82',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'admit',
      text: 'Is my admit rate declining, or is the drop in admits purely a function of fewer applicants?',
    },
    {
      id: 'monitoring-prompt-83',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'admit',
      text: 'How do my admits break down by distance band and income category compared to last year?',
    },
    {
      id: 'monitoring-prompt-84',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'admit',
      text: 'Are there any segments where admit activity has notably accelerated or stalled in the past week?',
    },
    {
      id: 'monitoring-prompt-85',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'admit',
      text: 'Have any segments (income, distance, source) seen a meaningful shift in admit volume over the last 30 days?',
    },
    {
      id: 'monitoring-prompt-86',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'admit',
      text: 'What was the academic profile (SAT, ACT, GPA) of my full admitted class last cycle vs. the cycle before?',
    },
    {
      id: 'monitoring-prompt-87',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'netDeposit',
      text: 'What is my point-in-time net deposit distribution by consumer income band compared to my cohort?',
    },
    {
      id: 'monitoring-prompt-88',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'netDeposit',
      text: 'Which student segments are depositing this week — are they different from the deposits I had 7 days ago?',
    },
    {
      id: 'monitoring-prompt-89',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'netDeposit',
      text: 'Has the composition of my deposited class (income, distance, academic profile) shifted over the last 30 days?',
    },
    {
      id: 'monitoring-prompt-90',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'netDeposit',
      text: 'How did the composition of my enrolled class (in-state vs. out-of-state, international, income) shift from the prior cycle?',
    },
    {
      id: 'monitoring-prompt-91',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'prospect',
      text: 'Which prospect sources are driving the year-over-year decline in my prospect pool?',
    },
    {
      id: 'monitoring-prompt-92',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'prospect',
      text: 'How does my prospect pool break down by distance band and consumer income compared to last year?',
    },
    {
      id: 'monitoring-prompt-93',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'prospect',
      text: 'Which prospect sources contributed the most new names this past week?',
    },
    {
      id: 'monitoring-prompt-94',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'prospect',
      text: 'How many prospects have I added in the last 30 days, and is that pace faster or slower than the same window last year?',
    },
    {
      id: 'monitoring-prompt-95',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'prospect',
      text: 'Which prospect sources grew or shrank the most across the full prior year?',
    },
    {
      id: 'monitoring-prompt-96',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'financial-aid',
      text: 'How does my FAFSA filer volume compare to the same point last year, and which segments show the largest decline?',
    },
    {
      id: 'monitoring-prompt-97',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'financial-aid',
      text: 'How does my filer volume break down by income band and distance compared to last year?',
    },
    {
      id: 'monitoring-prompt-98',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'How many new FAFSA filers appeared in the last 7 days, and how does that compare to the same week last year?',
    },
    {
      id: 'monitoring-prompt-99',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'Is my weekly filer pace picking up or still lagging behind last year?',
    },
    {
      id: 'monitoring-prompt-100',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'Is my year-over-year filer gap closing or widening compared to where it was 30 days ago?',
    },
    {
      id: 'monitoring-prompt-101',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'financial-aid',
      text: 'How many new filers have I gained in the last 30 days compared to the same window last year?',
    },
    {
      id: 'monitoring-prompt-102',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'financial-aid',
      text: 'How did my full-year filer volume break down by income band and distance for the completed cycle?',
    },
    {
      id: 'monitoring-prompt-103',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'retention',
      text: 'What is my current retention count, and how does my historical persistence rate compare to the prior cycle?',
    },
    {
      id: 'monitoring-prompt-104',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'volume',
      card: 'retention',
      text: 'Which student segments (income, distance, academic profile) have the lowest retention rates?',
    },
    {
      id: 'monitoring-prompt-105',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'retention',
      text: 'Has my retention count changed in the last 7 days — have any students dropped or been added?',
    },
    {
      id: 'monitoring-prompt-106',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'volume',
      card: 'retention',
      text: 'Are there specific segments where retention losses have accelerated this past week?',
    },
    {
      id: 'monitoring-prompt-107',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'retention',
      text: 'How has my retention count trended over the last 30 days — is attrition slowing or continuing?',
    },
    {
      id: 'monitoring-prompt-108',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'volume',
      card: 'retention',
      text: 'Which student segments have seen the most retention movement over the past month?',
    },
    {
      id: 'monitoring-prompt-109',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'volume',
      card: 'retention',
      text: 'Which student segments had the highest and lowest retention rates in the completed cycle?',
    },
    {
      id: 'monitoring-prompt-110',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'application',
      text: 'Which inquiry sources have the highest and lowest app rates, and how have those rates changed year over year?',
    },
    {
      id: 'monitoring-prompt-111',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'application',
      text: 'How does my app rate vary by consumer income band compared to last year?',
    },
    {
      id: 'monitoring-prompt-112',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'application',
      text: 'Has my app rate moved in the last 7 days, and which segments are driving the change?',
    },
    {
      id: 'monitoring-prompt-113',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'application',
      text: 'Are any inquiry sources showing a notable app rate spike or drop this past week?',
    },
    {
      id: 'monitoring-prompt-114',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'application',
      text: 'How has my app rate trended over the last 30 days — is it improving, flat, or declining?',
    },
    {
      id: 'monitoring-prompt-115',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'application',
      text: 'Which segments have seen the biggest app rate improvement or decline over the past month?',
    },
    {
      id: 'monitoring-prompt-116',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'application',
      text: 'Which inquiry sources had the highest and lowest full-year app rates last cycle?',
    },
    {
      id: 'monitoring-prompt-117',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'admit',
      text: 'Has my admit rate changed year over year, or is the decline in admits driven entirely by fewer applicants?',
    },
    {
      id: 'monitoring-prompt-118',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'admit',
      text: 'What is my admit rate for international students compared to domestic?',
    },
    {
      id: 'monitoring-prompt-119',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'admit',
      text: 'Has my admit rate shifted in the last 7 days — am I becoming more or less selective week over week?',
    },
    {
      id: 'monitoring-prompt-120',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'admit',
      text: 'Are there segments where my admit rate has moved notably this past week?',
    },
    {
      id: 'monitoring-prompt-121',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'admit',
      text: 'How has my admit rate moved over the last 30 days — am I trending more or less selective?',
    },
    {
      id: 'monitoring-prompt-122',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'admit',
      text: 'Which segments have seen the biggest admit rate shift over the past month?',
    },
    {
      id: 'monitoring-prompt-123',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'admit',
      text: 'What was my final admit rate last cycle — did I become more or less selective than the cycle before?',
    },
    {
      id: 'monitoring-prompt-124',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'admit',
      text: 'How did my admit rate vary by income band and distance for the full prior year?',
    },
    {
      id: 'monitoring-prompt-125',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'netDeposit',
      text: 'Which inquiry sources have the highest and lowest yield rates so far this cycle?',
    },
    {
      id: 'monitoring-prompt-126',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'netDeposit',
      text: 'How does my yield rate vary by in-state vs. out-of-state compared to last year?',
    },
    {
      id: 'monitoring-prompt-127',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'How has my yield rate changed in the last 7 days, and which segments are moving it?',
    },
    {
      id: 'monitoring-prompt-128',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'Are any income or distance segments showing a notable yield rate shift this past week?',
    },
    {
      id: 'monitoring-prompt-129',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'Is my yield rate gap vs. last year getting better or worse over the past month?',
    },
    {
      id: 'monitoring-prompt-130',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'netDeposit',
      text: 'How has my yield rate changed over the last 30 days, and which segments are driving the movement?',
    },
    {
      id: 'monitoring-prompt-131',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'netDeposit',
      text: 'Which inquiry sources had the highest and lowest full-year yield rates last cycle?',
    },
    {
      id: 'monitoring-prompt-132',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'inquiry',
      text: 'What is my inquiry rate (prospect-to-inquiry conversion), and how does it compare to the same point last year?',
    },
    {
      id: 'monitoring-prompt-133',
      scope: 'criticalElement',
      compareTo: 'point-in-time',
      view: 'rate',
      card: 'inquiry',
      text: 'Which prospect sources have the highest and lowest inquiry conversion rates this cycle?',
    },
    {
      id: 'monitoring-prompt-134',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'inquiry',
      text: 'Has my inquiry rate shifted in the last 7 days — are new prospects converting to inquiries faster or slower?',
    },
    {
      id: 'monitoring-prompt-135',
      scope: 'criticalElement',
      compareTo: 'last-7-days',
      view: 'rate',
      card: 'inquiry',
      text: 'Are any prospect sources showing a notable inquiry rate change this past week?',
    },
    {
      id: 'monitoring-prompt-136',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'inquiry',
      text: 'How has my inquiry rate trended over the last 30 days — is prospect-to-inquiry conversion improving?',
    },
    {
      id: 'monitoring-prompt-137',
      scope: 'criticalElement',
      compareTo: 'last-30-days',
      view: 'rate',
      card: 'inquiry',
      text: 'Which prospect sources have seen the biggest inquiry rate shift over the past month?',
    },
    {
      id: 'monitoring-prompt-138',
      scope: 'criticalElement',
      compareTo: 'prior-year',
      view: 'rate',
      card: 'inquiry',
      text: 'Which prospect sources had the highest and lowest full-year inquiry conversion rates?',
    },
    {
      id: 'monitoring-prompt-139',
      scope: 'topOfFunnel',
      compareTo: 'point-in-time',
      view: 'any',
      card: null,
      text: 'Where in my funnel am I losing the most students year over year — inquiry, application, admit, or deposit?',
    },
    {
      id: 'monitoring-prompt-140',
      scope: 'topOfFunnel',
      compareTo: 'point-in-time',
      view: 'any',
      card: null,
      text: 'What are my current goals and critical elements for this entering class?',
    },
    {
      id: 'monitoring-prompt-141',
      scope: 'topOfFunnel',
      compareTo: 'point-in-time',
      view: 'any',
      card: null,
      text: 'Which inquiry sources are producing the strongest end-to-end conversion from inquiry to deposit this cycle?',
    },
    {
      id: 'monitoring-prompt-142',
      scope: 'topOfFunnel',
      compareTo: 'last-7-days',
      view: 'any',
      card: null,
      text: 'Which stage of my funnel saw the most movement in the last 7 days?',
    },
    {
      id: 'monitoring-prompt-143',
      scope: 'topOfFunnel',
      compareTo: 'last-7-days',
      view: 'any',
      card: null,
      text: 'Has my weekly pace improved or declined across the funnel compared to the same week last year?',
    },
    {
      id: 'monitoring-prompt-144',
      scope: 'topOfFunnel',
      compareTo: 'last-30-days',
      view: 'any',
      card: null,
      text: 'Is my year-over-year gap widening or narrowing at each stage of the funnel over the past 30 days?',
    },
    {
      id: 'monitoring-prompt-145',
      scope: 'topOfFunnel',
      compareTo: 'last-30-days',
      view: 'any',
      card: null,
      text: 'Which funnel stage has shown the most momentum shift over the past month?',
    },
    {
      id: 'monitoring-prompt-146',
      scope: 'topOfFunnel',
      compareTo: 'prior-year',
      view: 'any',
      card: null,
      text: 'How did my full funnel — inquiry through enrollment — compare last cycle vs. the cycle before?',
    },
    {
      id: 'monitoring-prompt-147',
      scope: 'topOfFunnel',
      compareTo: 'prior-year',
      view: 'any',
      card: null,
      text: 'Which inquiry sources produced the highest yield rates across the full prior year?',
    },
    {
      id: 'monitoring-prompt-148',
      scope: 'criticalElements',
      compareTo: 'point-in-time',
      view: 'any',
      card: null,
      text: 'How are each of my critical element populations performing year over year at this point in the cycle?',
    },
    {
      id: 'monitoring-prompt-149',
      scope: 'criticalElements',
      compareTo: 'point-in-time',
      view: 'any',
      card: null,
      text: 'Which of my critical elements is most at risk of missing its target based on current funnel pacing?',
    },
    {
      id: 'monitoring-prompt-150',
      scope: 'criticalElements',
      compareTo: 'point-in-time',
      view: 'any',
      card: null,
      text: 'Are any of my critical element populations showing stronger or weaker conversion rates than the overall pool?',
    },
    {
      id: 'monitoring-prompt-151',
      scope: 'criticalElements',
      compareTo: 'last-7-days',
      view: 'any',
      card: null,
      text: 'Have any of my critical element populations seen a notable shift in the last 7 days?',
    },
    {
      id: 'monitoring-prompt-152',
      scope: 'criticalElements',
      compareTo: 'last-7-days',
      view: 'any',
      card: null,
      text: 'Which critical element population added the most volume this past week?',
    },
    {
      id: 'monitoring-prompt-153',
      scope: 'criticalElements',
      compareTo: 'last-30-days',
      view: 'any',
      card: null,
      text: 'Which of my critical elements are gaining or losing ground over the past 30 days?',
    },
    {
      id: 'monitoring-prompt-154',
      scope: 'criticalElements',
      compareTo: 'last-30-days',
      view: 'any',
      card: null,
      text: 'Are any critical element populations closing their year-over-year gap faster than others?',
    },
    {
      id: 'monitoring-prompt-155',
      scope: 'criticalElements',
      compareTo: 'prior-year',
      view: 'any',
      card: null,
      text: 'How did each of my critical element populations perform against their targets in the completed cycle?',
    },
    {
      id: 'monitoring-prompt-156',
      scope: 'criticalElements',
      compareTo: 'prior-year',
      view: 'any',
      card: null,
      text: 'Which critical elements exceeded expectations and which fell short last cycle?',
    },
  ];
